import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { store } from '../../store';
import { router } from '../../router';
import { api } from '../../api';
import { t } from '../../i18n';
import { icon } from '../ui/icons';

/** <cadence-header> — top bar: mobile menu toggle, global search, user identity, theme + logout. */
@customElement('cadence-header')
export class CadenceHeader extends LitElement {
  @state() private version = 0;
  @state() private query = '';
  @state() private results: any[] = [];
  @state() private open = false;
  private debounce = 0;
  private unsub?: () => void;

  static styles = css`
    header {
      height: var(--header-height);
      display: flex; align-items: center; gap: var(--space-3);
      padding: 0 var(--space-4);
      background: var(--color-bg-primary);
      border-bottom: 1px solid var(--color-border);
      position: sticky; top: 0; z-index: 30;
    }
    .hamburger {
      display: none; background: none; border: none; cursor: pointer;
      color: var(--color-text-primary); padding: 6px;
    }
    .spacer { flex: 1; }
    .search { position: relative; width: 320px; max-width: 40vw; }
    .search input {
      width: 100%; font: inherit; padding: 9px 12px; border-radius: var(--radius-md);
      border: 1px solid var(--color-border); background: var(--color-bg-secondary); color: var(--color-text-primary);
    }
    .search input:focus { outline: none; border-color: var(--color-primary); background: var(--color-bg-primary); }
    .results {
      position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 50;
      background: var(--color-bg-primary); border: 1px solid var(--color-border);
      border-radius: var(--radius-md); box-shadow: var(--shadow-lg); overflow: hidden;
    }
    .results button {
      display: flex; flex-direction: column; gap: 2px; width: 100%; text-align: left;
      background: none; border: none; padding: 10px 14px; cursor: pointer; color: var(--color-text-primary); font: inherit;
    }
    .results button:hover { background: var(--color-bg-secondary); }
    .results .email { color: var(--color-text-secondary); font-size: 0.78rem; }
    .results .none { padding: 12px 14px; color: var(--color-text-secondary); font-size: 0.85rem; }
    @media (max-width: 768px) { .search { display: none; } }
    .icon-btn {
      background: none; border: 1px solid var(--color-border); cursor: pointer;
      color: var(--color-text-secondary); border-radius: var(--radius-md);
      width: 38px; height: 38px; display: inline-grid; place-items: center;
      transition: border-color 0.15s, color 0.15s;
    }
    .icon-btn:hover { border-color: var(--color-primary); color: var(--color-primary); }
    .user { display: flex; align-items: center; gap: 10px; }
    .avatar {
      width: 34px; height: 34px; border-radius: 50%; background: var(--color-primary);
      color: #fff; display: grid; place-items: center; font-weight: 700; font-size: 0.85rem;
    }
    .name { font-weight: 600; font-size: 0.9rem; }
    .email { color: var(--color-text-secondary); font-size: 0.78rem; }
    .who { display: flex; flex-direction: column; line-height: 1.2; }
    svg { width: 20px; height: 20px; }
    @media (max-width: 768px) {
      .hamburger { display: inline-flex; }
      .who { display: none; }
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.unsub = store.subscribe(() => { this.version++; });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsub?.();
  }

  private async toggleTheme() {
    const dark = !store.get().darkMode;
    store.applyTheme(dark);
    try { await api.patch('/auth/me', { dark_mode: dark }); } catch { /* preference still applied locally */ }
  }

  private onSearch(value: string) {
    this.query = value;
    clearTimeout(this.debounce);
    if (value.trim().length < 2) { this.results = []; this.open = false; return; }
    this.debounce = window.setTimeout(async () => {
      try {
        const data = await api.get<{ students: any[] }>(`/students?search=${encodeURIComponent(value)}&limit=6`);
        this.results = data.students;
        this.open = true;
      } catch { /* ignore */ }
    }, 250);
  }

  private pick(id: string) {
    this.open = false; this.query = ''; this.results = [];
    router.navigate(`/admin/students/${id}`);
  }

  render() {
    const { darkMode } = store.get();
    return html`
      <header>
        <button
          class="hamburger"
          aria-label=${t('common.actions.openMenu')}
          @click=${() => this.dispatchEvent(new CustomEvent('toggle-sidebar', { bubbles: true, composed: true }))}
        >
          ${icon('menu', 20)}
        </button>

        <div class="search">
          <input
            type="search"
            role="searchbox"
            aria-label=${t('common.search.placeholder')}
            placeholder=${t('common.search.placeholder')}
            .value=${this.query}
            @input=${(e: Event) => this.onSearch((e.target as HTMLInputElement).value)}
            @focus=${() => { if (this.results.length) this.open = true; }}
            @blur=${() => setTimeout(() => (this.open = false), 150)}
          />
          ${this.open ? html`
            <div class="results">
              ${this.results.length
                ? this.results.map((s) => html`
                    <button @mousedown=${() => this.pick(s.id)}>
                      <span>${s.first_name} ${s.last_name}</span>
                      <span class="email">${s.email}</span>
                    </button>`)
                : html`<div class="none">${t('common.search.noResults')}</div>`}
            </div>` : ''}
        </div>

        <div class="spacer"></div>

        <cadence-notification-bell></cadence-notification-bell>

        <cadence-lang-menu></cadence-lang-menu>

        <button class="icon-btn" aria-label=${t('common.actions.toggleTheme')} title=${t('common.actions.toggleTheme')} @click=${this.toggleTheme}>
          ${darkMode ? icon('sun', 20) : icon('moon', 20)}
        </button>

        <cadence-user-menu logout-path="/login"></cadence-user-menu>
      </header>
    `;
  }
}
